import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { NativeStackScreenProps } from '@react-navigation/native-stack';

import { Button } from '@/components/ButtonComponent';
import { Screen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { SocialCurrencyToggle } from '@/components/SocialCurrencyToggle';
import { SocialWalletBalanceButton } from '@/components/SocialWalletBalanceButton';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { useProductTransitionStore } from '@/feature/product-transition/use-product-transition-store';
import { useCurrencyGuidance } from '@/hooks/use-currency-guidance';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useRootNavigation } from '@/hooks/use-root-navigation';
import { SocialCurrency, useSocialCurrencyStore } from '@/hooks/use-social-currency';
import { navigateToKyc } from '@/navigation/navigation';
import { DepositStackParamList } from '@/navigation/types';

import { CoinPackageList } from '../components/CoinPackageList';
import { useCurrencyOverviewModal } from '../components/CurrencyOverviewModalProvider';
import { InAppPurchaseList } from '../components/InAppPurchaseList';

type ScreenProps = NativeStackScreenProps<DepositStackParamList, 'CoinPackageScreen'>;

export const CoinPackageScreen = ({ navigation, route }: ScreenProps) => {
    const activeCurrency = useSocialCurrencyStore(state => state.activeCurrency);
    const { shouldShow, markAsShown } = useCurrencyGuidance();
    const { showCurrencyOverviewModal } = useCurrencyOverviewModal();
    const rootNavigation = useRootNavigation();
    const { t } = useTranslation('common');
    const product = useJurisdictionStore(state => state.product);
    const isTrustlyKyc = Boolean(route.params?.isTrustlyKyc);
    // Used to determine whether the skip option is displayed if accessed via KYC
    const isOnboarding = isTrustlyKyc || Boolean(route.params?.isFromKyc);

    useEffect(() => {
        const { activeCurrency: currentCurrency, actions } = useSocialCurrencyStore.getState();
        if (currentCurrency === SocialCurrency.COINS) {
            actions.setActiveCurrency(SocialCurrency.CASH);
        }
    }, []);

    useEffect(() => {
        if (shouldShow) {
            showCurrencyOverviewModal({ currency: activeCurrency });
            markAsShown();
        }
    }, [shouldShow, markAsShown, activeCurrency, showCurrencyOverviewModal]);

    const goBack = useCallback(() => {
        rootNavigation.goBack();
    }, [rootNavigation]);

    const handleSkip = useCallback(() => {
        BetrAnalytics.trackProductEvent(AnalyticsEvent.FTD_SKIP);
        if (isTrustlyKyc) {
            // Trustly still has to verify the user's identity, so run it in ID-only mode (amount 0.00).
            navigateToKyc(navigation, { screen: 'TrustlyWebview', params: { isTrustlyIdOnly: true } });
            return;
        }
        useProductTransitionStore.getState().actions.triggerTransition(product);
    }, [isTrustlyKyc, navigation, product]);

    return (
        <Screen edges={['top', 'bottom']}>
            <ScreenNavBar
                title={'Shop'}
                alignTitleLeft={true}
                closeIconMode={'close'}
                // the shop is not always the first screen of the stack, so dismiss the whole stack
                onClose={goBack}
                titleVariant="headlineMedium"
                rightIcon={
                    <Row gap="s16">
                        <SocialWalletBalanceButton />
                        <SocialCurrencyToggle />
                    </Row>
                }
                height={48}
            />
            <Box flex={1} mt={'s24'}>
                {activeCurrency === SocialCurrency.CASH ? (
                    <CoinPackageList isTrustlyKyc={isTrustlyKyc} />
                ) : (
                    <InAppPurchaseList />
                )}
            </Box>
            {isOnboarding ? (
                <Box borderTopWidth={1} borderColor={ds.colorToken.border.subtle} padding="s16">
                    <Button label={t('skip')} hierarchy="secondary" onPress={handleSkip} testID="coin-package-skip" />
                </Box>
            ) : null}
        </Screen>
    );
};
